/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grow} from '@material-ui/core';
import {SnackbarKey, useSnackbar} from 'notistack';
import {AlertSeverity} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {getUserNotificationsPreferences} from '../helpers';
import {NotificationAlert} from '../notifications/NotificationAlert';
import {useAuthenticatedUser} from '../pages/admin/users/useAuthenticatedUser';
import {
  markAsRead,
  removeToastNotification,
  setNotificationPanelState,
  setNotificationsPanelStatus
} from './state';

export interface ToastNotificationsContainerType {
  cancelToastDismiss: any[];
  setCancelToastDismiss: Function;
}

/**
 * Checks if User has enabled preference to view notification pop-up of this type
 */
export const isNotificationTypeAvailable = (severity: AlertSeverity, preferences: any) => {
  const alertSeverityMap = {
    [AlertSeverity.CRITICAL] : preferences[Preference.showUrgentNotifications],
    [AlertSeverity.HIGH]     : preferences[Preference.showGeneralNotifications],
    [AlertSeverity.MEDIUM]   : preferences[Preference.showTimeRelatedNotifications],
    [AlertSeverity.LOW]      : preferences[Preference.showConnectivityNotifications],
    [AlertSeverity.SYSTEM]   : preferences[Preference.showScheduledEventsNotifications]
  };
  return Boolean(alertSeverityMap[severity]);
};

export const ToastNotificationsContainer = ({
  cancelToastDismiss,
  setCancelToastDismiss
}: ToastNotificationsContainerType) => {
  const dispatch                  = useDispatch();
  const detailsState              = useSelector(({notificationsPanel}) => notificationsPanel.panel.state);
  const latestToastNotifications  = useSelector(({notificationsPanel}) => notificationsPanel.latest);
  const user                      = useAuthenticatedUser();
  const [exitingSnackBar, setExitingSnackbar]                = useState(false);
  const [activeToasts, setActiveToasts]                      = useState([]);
  const [userNotificationsPreferences, setUserNotifications] = useState([]);
  const {enqueueSnackbar, closeSnackbar}                     = useSnackbar();
  const {isPhone, isDesktop}                                 = useMedia();

  useEffect(() => {
    setUserNotifications(getUserNotificationsPreferences());
  }, [user]);

  useEffect(() => {
    if (userNotificationsPreferences?.[Preference.showNotifications]) {
      latestToastNotifications?.forEach((notification, index) => {
        const {
          category,
          isRead,
          alertConfigurationName,
          severity = AlertSeverity.MEDIUM,
          createdOn,
          uuid,
          assetName,
          alertType
        } = notification;
        if (exitingSnackBar) return;
        const {title: alertTypeTitle} = alertType;
        const matchingClick           = detailsState.find(clicked => clicked.id === uuid);
        const isDetailsOpen           = matchingClick?.open;
        const isDetailsLoading        = matchingClick?.loading;
        const isUrgent                = severity === AlertSeverity.CRITICAL;

        const onToastExit = (key: SnackbarKey) => {
          key && closeSnackbar(key);
          dispatch(removeToastNotification(index));
          clearCancelDismiss(notification.uuid);
          removeActiveToast(notification.uuid);
          // this prevents a second snackbar from getting
          // queued immediately after the first one exits,
          // allowing for the transition to finish. This happens
          // unexpectedly when more than one notifications are
          // retrieved from the call to latest API
          setExitingSnackbar(true);
          setTimeout(() => {
            setExitingSnackbar(false);
          }, 1000);
        };

        function cancelActiveToast(uuid) {
          const match = activeToasts.find(active => active[uuid]);
          if (match) {
            clearTimeout(match[uuid]);
          }
        }

        if (isNotificationTypeAvailable(severity, userNotificationsPreferences)) {
          const key = enqueueSnackbar(`${notification.assetName}`, {
            autoHideDuration    : null,
            TransitionComponent : GrowTransition,
            transitionDuration  : {enter: 1000, exit: 1000},
            onExited            : function() {
              onToastExit(0);
            },
            content : (key: SnackbarKey) => (
              <NotificationAlert id={key}
                toastId={uuid}
                type={alertTypeTitle}
                text={assetName}
                subText={alertConfigurationName}
                read={isRead}
                timeStamp={createdOn}
                isUrgent={isUrgent}
                onMoreClick={readMore}
                onLessClick={readLess}
                onReviewClick={onReviewNotification}
                moreInfoLoading={isDetailsLoading}
                showMoreInfo={isDetailsOpen}
                notification={notification}
                onDoubleClick={cancelActiveToast} />
            )
          });
          activeToasts.push({
            [notification.uuid] : setTimeout(() => {
              onToastExit(key);
            }, 5000)
          });
        }
      });
    }
  }, [latestToastNotifications]);

  useEffect(() => {
    cancelToastDismiss.forEach(toast => {
      const match = activeToasts.find(active => active[toast]);
      if (match) {
        clearTimeout(match[toast]);
      }
      clearCancelDismiss(toast);
    });
  }, [cancelToastDismiss]);

  function clearCancelDismiss(uuid) {
    const cancelMatches = cancelToastDismiss.filter(dismiss => uuid !== dismiss);
    setCancelToastDismiss(cancelMatches);
  };

  function removeActiveToast(uuid) {
    const removedActive = activeToasts.filter(active => !active[uuid]);
    setActiveToasts(removedActive);
  };

  function GrowTransition(props) {
    return <Grow {...props} />;
  }

  function onReviewNotification(notification) {
    isDesktop && setCancelToastDismiss([...cancelToastDismiss, notification?.uuid]);
    isPhone && closeSnackbar(notification?.uuid);
    isPhone && dispatch(setNotificationsPanelStatus(false));
    dispatch(markAsRead(notification));
  }

  function readMore(state) {
    dispatch(setNotificationPanelState(state));
  }
  function readLess(state) {
    dispatch(setNotificationPanelState(state));
  }

  return null;
};

export default ToastNotificationsContainer;
